import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  ChevronRight,
  Person,
  Logout,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { logout, getCurrentUser } from '../store/slices/authSlice';
import Logo from './common/Logo';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
}));

const Header: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { subscription } = useSelector((state: RootState) => state.subscription);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if (user && !user.emailVerified) {
        dispatch(getCurrentUser());
      }
    }, 30000);
    return () => clearInterval(refreshInterval);
  }, [dispatch, user?.emailVerified]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleLogout = () => {
    dispatch(logout());
    handleUserMenuClose();
  };

  const getInitials = () => {
    if (!user) return 'U';
    const name = user.name || '';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return (name.charAt(0) || 'U').toUpperCase();
  };

  /** Scientific / DSS order — not marketing order */
  const navItems = [
    { text: 'Workspace', path: '/dashboard' },
    { text: 'Water', path: '/water-quality' },
    { text: 'Crop', path: '/formulation' },
    { text: 'Salts', path: '/salt-library' },
    { text: 'Recipe', path: '/stock-solutions' },
    { text: 'Results', path: '/results' },
    ...(user?.role === 'ADMIN' ? [{ text: 'Admin', path: '/admin' }] : []),
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === '/water-quality') {
      return (
        location.pathname.startsWith('/water-quality') ||
        location.pathname.startsWith('/water-profile')
      );
    }
    if (path === '/results') {
      return (
        location.pathname.startsWith('/results') ||
        location.pathname.startsWith('/calculation')
      );
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={handleDrawerToggle} aria-label="close menu">
          <ChevronRight />
        </IconButton>
      </Box>
      <List dense>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              selected={isActive(item.path)}
            >
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: isActive(item.path) ? 600 : 400,
                  color: isActive(item.path) ? 'primary' : 'textPrimary',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  return (
    <StyledAppBar position="sticky">
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56, gap: 1 }}>
        <Box
          component={Link}
          to={isAuthenticated ? '/dashboard' : '/'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            textDecoration: 'none',
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          <Logo type="flahafast" size="small" />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, letterSpacing: '-0.02em', display: { xs: 'none', sm: 'block' } }}
          >
            FlahaFAST
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: 'none', lg: 'block' }, ml: 0.5, fontWeight: 500 }}
          >
            DSS
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {navItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link as any}
                  to={item.path}
                  size="small"
                  sx={{
                    minWidth: 0,
                    px: 1.25,
                    color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive(item.path) ? 600 : 400,
                    borderBottom: isActive(item.path) ? 2 : 0,
                    borderColor: 'primary.main',
                    borderRadius: 0,
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}

          {isAuthenticated && user ? (
            <>
              {subscription?.tier && subscription.tier !== 'FREE' && (
                <Chip
                  label={subscription.tier}
                  size="small"
                  variant="outlined"
                  sx={{ display: { xs: 'none', md: 'inline-flex' }, height: 24 }}
                />
              )}
              {!user.emailVerified && (
                <Chip
                  label="Verify email"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ display: { xs: 'none', sm: 'inline-flex' }, height: 24 }}
                />
              )}
              <IconButton onClick={handleUserMenuOpen} sx={{ p: 0.5 }} aria-label="Account menu">
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    fontSize: '0.8rem',
                  }}
                >
                  {getInitials()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                onClick={handleUserMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem component={Link} to="/profile" disabled>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {user.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem component={Link} to="/profile">
                  <Person fontSize="small" sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout fontSize="small" sx={{ mr: 1 }} />
                  Sign out
                </MenuItem>
              </Menu>
            </>
          ) : null}

          {isMobile && (
            <>
              <IconButton
                color="inherit"
                aria-label="Open menu"
                edge="end"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="right"
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: { width: 260 } }}
              >
                {drawer}
              </Drawer>
            </>
          )}
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;
